import { ControlTypeEnum, AccessLevel } from '@/types';
import config from '@/config';

// API Configuration
export const API_BASE_URL = config.REACT_APP_AGROKOLL_API_URL;

// App Configuration
export const APP_NAME = 'Agronod';
export const APP_VERSION = '1.0.0';
export const APP_DESCRIPTION = 'Document management platform for Swedish farmers';

// Authentication
export const TOKEN_STORAGE_KEY = 'kontrollresan_token';
export const USER_STORAGE_KEY = 'kontrollresan_user';
export const REMEMBER_ME_STORAGE_KEY = 'kontrollresan_remember_me';

// Pagination
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

// File Upload
export const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'application/vnd.ms-excel', // xls
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'application/msword', // doc
  'text/csv',
];

// Control Types with Swedish labels
export const CONTROL_TYPE_LABELS: Record<ControlTypeEnum, string> = {
  [ControlTypeEnum.KRAV_ORGANIC]: 'KRAV Ekologisk',
  [ControlTypeEnum.EU_ORGANIC]: 'EU Ekologisk',
  [ControlTypeEnum.IP_INTEGRATED]: 'IP Integrerad produktion',
  [ControlTypeEnum.GOVERNMENT_INSPECTION]: 'Myndighetskontroll',
  [ControlTypeEnum.INTERNAL_AUDIT]: 'Intern revision',
  [ControlTypeEnum.CUSTOMER_AUDIT]: 'Kundrevision',
  [ControlTypeEnum.CERTIFICATION_RENEWAL]: 'Certifikatförnyelse',
  [ControlTypeEnum.SPOT_CHECK]: 'Stickprovskontroll',
  [ControlTypeEnum.COMPLAINT_INVESTIGATION]: 'Klagomålsutredning',
  [ControlTypeEnum.OTHER]: 'Övrigt',
};

// Access Levels with Swedish labels
export const ACCESS_LEVEL_LABELS: Record<AccessLevel, string> = {
  [AccessLevel.VIEW]: 'Visa',
  [AccessLevel.DOWNLOAD]: 'Ladda ner',
  [AccessLevel.COMMENT]: 'Kommentera',
};

// Production Types
export const PRODUCTION_TYPES = [
  { value: 'ORGANIC_VEGETABLES', label: 'Ekologiska grönsaker' },
  { value: 'CONVENTIONAL_VEGETABLES', label: 'Konventionella grönsaker' },
  { value: 'DAIRY', label: 'Mjölkproduktion' },
  { value: 'BEEF_CATTLE', label: 'Nötkött' },
  { value: 'SHEEP', label: 'Får' },
  { value: 'PORK', label: 'Gris' },
  { value: 'POULTRY', label: 'Fjäderfä' },
  { value: 'GRAIN', label: 'Spannmål' },
  { value: 'POTATOES', label: 'Potatis' },
  { value: 'FRUITS', label: 'Frukt' },
  { value: 'BERRIES', label: 'Bär' },
  { value: 'FORESTRY', label: 'Skogsbruk' },
  { value: 'OTHER', label: 'Övrigt' },
];

// Certification Types
export const CERTIFICATION_TYPES = [
  { value: 'KRAV', label: 'KRAV' },
  { value: 'EU_ORGANIC', label: 'EU Ekologisk' },
  { value: 'IP', label: 'IP Integrerad produktion' },
  { value: 'SVENSKT_SIGILL', label: 'Svenskt Sigill' },
  { value: 'MSC', label: 'MSC' },
  { value: 'ASC', label: 'ASC' },
  { value: 'FAIRTRADE', label: 'Fairtrade' },
  { value: 'RAINFOREST_ALLIANCE', label: 'Rainforest Alliance' },
  { value: 'GLOBAL_GAP', label: 'GlobalGAP' },
  { value: 'OTHER', label: 'Övrigt' },
];

// Swedish Regions
export const SWEDISH_REGIONS = [
  { value: 'Stockholm', label: 'Stockholm' },
  { value: 'Uppsala', label: 'Uppsala' },
  { value: 'Södermanland', label: 'Södermanland' },
  { value: 'Östergötland', label: 'Östergötland' },
  { value: 'Jönköping', label: 'Jönköping' },
  { value: 'Kronoberg', label: 'Kronoberg' },
  { value: 'Kalmar', label: 'Kalmar' },
  { value: 'Gotland', label: 'Gotland' },
  { value: 'Blekinge', label: 'Blekinge' },
  { value: 'Skåne', label: 'Skåne' },
  { value: 'Halland', label: 'Halland' },
  { value: 'Västra Götaland', label: 'Västra Götaland' },
  { value: 'Värmland', label: 'Värmland' },
  { value: 'Örebro', label: 'Örebro' },
  { value: 'Västmanland', label: 'Västmanland' },
  { value: 'Dalarna', label: 'Dalarna' },
  { value: 'Gävleborg', label: 'Gävleborg' },
  { value: 'Västernorrland', label: 'Västernorrland' },
  { value: 'Jämtland', label: 'Jämtland' },
  { value: 'Västerbotten', label: 'Västerbotten' },
  { value: 'Norrbotten', label: 'Norrbotten' },
];

// Date Formats
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';
export const DISPLAY_DATE_FORMAT = 'DD MMM YYYY';
export const DISPLAY_DATETIME_FORMAT = 'DD MMM YYYY HH:mm';

// Query Keys for React Query
export const QUERY_KEYS = {
  USER: ['user'],
  DOCUMENTS: ['documents'],
  DOCUMENT: (id: string) => ['document', id],
  DOCUMENT_SHARING: (id: string) => ['documentSharing', id],
  DOCUMENT_VERSIONS: (id: string) => ['documentVersions', id],
  CONTROLS: ['controls'],
  CONTROL: (id: string) => ['control', id],
  SHARES: ['shares'],
  SHARE: (id: string) => ['share', id],
  AUDIT_LOGS: ['auditLogs'],
} as const;

// Routes
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  DASHBOARD: '/dashboard',
  DOCUMENTS: '/documents',
  DOCUMENT_UPLOAD: '/documents/upload',
  CONTROLS: '/controls',
  CREATE_CONTROL: '/controls/create',
  CONTROL_CREATE: '/controls/create',
  CONTROL_DETAIL: (id: string) => `/controls/${id}`,
  SHARES: '/shares',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  SHARED_VIEW: (token: string) => `/shared/${token}`,

  // Public routes (no OIDC authentication required)
  PUBLIC_SHARE: (token: string) => `/share/${token}`,
  PUBLIC_SHARE_PREFIX: '/share/', // Trailing slash ensures /shares is not matched

  // Admin routes
  ADMIN: '/admin',
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_USERS: '/admin/users',
  ADMIN_CONTROL_TYPES: '/admin/control-types',
  ADMIN_ISSUERS: '/admin/issuers',
  ADMIN_TAGS: '/admin/tags',
  ADMIN_SYSTEM_HEALTH: '/admin/system-health',
  ADMIN_AUDIT_LOGS: '/admin/audit-logs',

  // Error pages
  UNAUTHORIZED: '/unauthorized',
} as const;

// Public route prefixes for authentication checks
// NOTE: OIDC callback routes (/authentication/*, /b2b/authentication/*) are NOT public
// They need AuthProvider to process the callback, so they're explicitly excluded in RealmRouter/RealmGuard
export const PUBLIC_ROUTES = [
  ROUTES.PUBLIC_SHARE_PREFIX, // Only /share/:token is truly public
] as const;

/**
 * Check whether a path is a public route that renders without OIDC authentication.
 *
 * Public pages have no AuthProvider in their tree and need no access token, so
 * nothing in the auth stack may redirect away from them.
 *
 * @param pathname - a router location pathname or `window.location.pathname`
 * @returns true when the route requires neither AuthProvider nor a token
 */
export const isPublicRoute = (pathname: string): boolean =>
  PUBLIC_ROUTES.some((prefix) => pathname.startsWith(prefix));

// Theme Colors (Agronod Design System)
export const THEME_COLORS = {
  primary: '#F2CB6C', // Agronod Yellow/Gold
  secondary: '#51697E', // Blue-Gray
  success: '#5D9D52', // Agronod Success Green
  warning: '#ED9135', // Agronod Warning Orange
  error: '#D4483E', // Agronod Error Red
  info: '#68523D', // Agronod Info Brown
  background: {
    default: '#F8F6F3', // Agronod Page Background
    paper: '#FFFFFF',
  },
  text: {
    primary: '#252321', // Agronod Black
    secondary: '#666461', // Agronod Dark Gray
  },
};

// Breakpoints for responsive design
export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Nätverksfel. Kontrollera din internetanslutning.',
  UNAUTHORIZED: 'Du är inte inloggad. Logga in för att fortsätta.',
  FORBIDDEN: 'Du har inte behörighet för denna åtgärd.',
  NOT_FOUND: 'Den begärda resursen kunde inte hittas.',
  SERVER_ERROR: 'Serverfel. Försök igen senare.',
  VALIDATION_ERROR: 'Validering misslyckades. Kontrollera dina uppgifter.',
  FILE_TOO_LARGE: 'Filen är för stor. Maximal storlek är 100MB.',
  INVALID_FILE_TYPE: 'Filtypen stöds inte.',
  GENERIC_ERROR: 'Ett fel uppstod. Försök igen senare.',
};